export const truncateDecimals = (
  value: number,
  decimals: number = 2
): string => {
  if (isNaN(value) || !isFinite(value)) {
    if (decimals === 0) {
      return "0";
    }
    return "0." + "0".repeat(decimals);
  }

  if (decimals === 0) {
    const wholeNumber = Math.floor(Math.abs(value));
    return (value < 0 ? "-" : "") + wholeNumber.toString();
  }

  const isNegative = value < 0;
  const absValue = Math.abs(value);
  const multiplier = Math.pow(10, decimals);
  const truncated = Math.floor(absValue * multiplier) / multiplier;
  const result = isNegative ? -truncated : truncated;

  const resultStr = result.toString();
  const parts = resultStr.split(".");

  if (parts.length === 1) {
    return resultStr + "." + "0".repeat(decimals);
  }

  let decimalPart = parts[1];

  if (decimalPart.length > decimals) {
    decimalPart = decimalPart.substring(0, decimals);
  }

  if (decimalPart.length < decimals) {
    decimalPart = decimalPart + "0".repeat(decimals - decimalPart.length);
  }

  if (!decimalPart || decimalPart.length === 0) {
    return parts[0] + "." + "0".repeat(decimals);
  }

  return parts[0] + "." + decimalPart;
};

export interface DecimalNumberLike {
  $numberDecimal: string;
}

export interface NumberFormattingOptions {
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
}

export interface CurrencyFormattingOptions extends NumberFormattingOptions {
  currencyDisplay?: "code" | "symbol";
}

export const parseNumericValue = (
  value: number | string | DecimalNumberLike | null | undefined
): number | null => {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === "string") {
    const cleaned = value.replace(/,/g, "").trim();
    if (!cleaned) {
      return null;
    }

    const parsed = parseFloat(cleaned);
    return Number.isFinite(parsed) ? parsed : null;
  }

  const parsed = parseFloat(value.$numberDecimal);
  return Number.isFinite(parsed) ? parsed : null;
};

export const formatNumberWithSpaces = (
  value: number | string | DecimalNumberLike,
  options: NumberFormattingOptions = {}
): string => {
  const parsed = parseNumericValue(value);
  const minimumFractionDigits = options.minimumFractionDigits ?? 2;
  const maximumFractionDigits =
    options.maximumFractionDigits ?? minimumFractionDigits;

  if (parsed === null) {
    const fallbackDecimals = Math.max(minimumFractionDigits, 0);
    if (fallbackDecimals === 0) {
      return "0";
    }
    return `0.${"0".repeat(fallbackDecimals)}`;
  }

  return parsed
    .toLocaleString("en-US", {
      minimumFractionDigits,
      maximumFractionDigits,
    })
    .replace(/,/g, " ");
};

export const formatCurrencyValue = (
  value: number | string | DecimalNumberLike,
  currency: string = "ZAR",
  options: CurrencyFormattingOptions = {}
): string => {
  const currencyCode = currency.toUpperCase();
  const prefix =
    options.currencyDisplay === "symbol"
      ? currencyCode === "ZAR"
        ? "R"
        : currencyCode
      : currencyCode;

  return `${prefix} ${formatNumberWithSpaces(value, options)}`;
};

/**
 * Truncates to decimals then formats the integer part with spaces as thousand separators.
 * e.g. 67123.456 -> "67 123.45"
 */
export const formatWithThousandSpaces = (
  value: number,
  decimals: number = 2
): string => {
  const truncated = truncateDecimals(value, decimals);
  const [intPart, decPart] = truncated.split(".");
  const withSpaces = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  return decPart != null ? `${withSpaces}.${decPart}` : withSpaces;
};

/**
 * Formats a number with significant digits, removing trailing zeros.
 * - For numbers >= 1000: max 2 decimal places
 * - For numbers >= 1: max 4 decimal places
 * - For numbers < 1: shows 4 significant digits after the first non-zero digit
 *
 * Examples:
 * - 0.00001 → "0.00001"
 * - 0.00111 → "0.0011"
 * - 0.123456 → "0.1235"
 * - 1234.56789 → "1234.57"
 */
export const formatWithSignificantDigits = (
  value: number,
  options: { minSignificant?: number; maxDecimals?: number } = {}
): string => {
  const { minSignificant = 4, maxDecimals = 8 } = options;

  if (isNaN(value) || !isFinite(value) || value === 0) {
    return "0";
  }

  const isNegative = value < 0;
  const absValue = Math.abs(value);

  // Large numbers (>= 1000): max 2 decimals
  if (absValue >= 1000) {
    const truncated = Math.floor(absValue * 100) / 100;
    let result = truncated.toString();
    // Remove trailing zeros after decimal
    if (result.includes(".")) {
      result = result.replace(/\.?0+$/, "");
    }
    return isNegative ? `-${result}` : result;
  }

  // Medium numbers (>= 1): max 4 decimals
  if (absValue >= 1) {
    const truncated = Math.floor(absValue * 10000) / 10000;
    let result = truncated.toString();
    // Remove trailing zeros after decimal
    if (result.includes(".")) {
      result = result.replace(/\.?0+$/, "");
    }
    return isNegative ? `-${result}` : result;
  }

  // Small numbers (< 1): find first significant digit and show minSignificant digits
  const absStr = absValue.toString();

  // Handle scientific notation
  if (absStr.includes("e")) {
    const formatted = absValue.toPrecision(minSignificant);
    const num = parseFloat(formatted);
    // Convert back to fixed notation if within range
    const decimalPlaces = Math.min(
      maxDecimals,
      Math.max(0, -Math.floor(Math.log10(num)) + minSignificant - 1)
    );
    let result = num.toFixed(decimalPlaces);
    // Remove trailing zeros
    result = result.replace(/\.?0+$/, "");
    return isNegative ? `-${result}` : result;
  }

  // Find position of first non-zero digit after decimal
  const decimalIndex = absStr.indexOf(".");
  if (decimalIndex === -1) {
    return isNegative ? `-${absStr}` : absStr;
  }

  const afterDecimal = absStr.substring(decimalIndex + 1);
  let firstNonZeroPos = 0;
  for (let i = 0; i < afterDecimal.length; i++) {
    if (afterDecimal.charAt(i) !== "0") {
      firstNonZeroPos = i;
      break;
    }
  }

  // Calculate how many decimal places we need
  const neededDecimals = Math.min(
    firstNonZeroPos + minSignificant,
    maxDecimals
  );

  // Truncate (not round) to the needed decimals
  const multiplier = Math.pow(10, neededDecimals);
  const truncated = Math.floor(absValue * multiplier) / multiplier;

  let result = truncated.toFixed(neededDecimals);

  // Remove trailing zeros after decimal point
  result = result.replace(/\.?0+$/, "");

  // Ensure we don't return just "0" for small values
  if (result === "0" && absValue > 0) {
    result = absValue.toPrecision(minSignificant);
    // Remove trailing zeros
    if (result.includes(".")) {
      result = result.replace(/\.?0+$/, "");
    }
  }

  return isNegative ? `-${result}` : result;
};

export const formatCompactNumber = (
  value: number | string | DecimalNumberLike,
  options: { maxDecimals?: number } = {}
): string => {
  const parsed = parseNumericValue(value);
  if (parsed === null) return "0";

  const rawMaxDecimals = options.maxDecimals;
  const maxDecimals =
    typeof rawMaxDecimals === "number" && Number.isFinite(rawMaxDecimals)
      ? Math.min(20, Math.max(0, Math.trunc(rawMaxDecimals)))
      : 1;
  const absValue = Math.abs(parsed);

  const formatScaled = (scaled: number, suffix: string): string => {
    if (Number.isInteger(scaled)) {
      return `${scaled.toFixed(0)}${suffix}`;
    }
    return `${scaled.toFixed(maxDecimals)}${suffix}`;
  };

  if (absValue >= 1_000_000_000_000) {
    return formatScaled(parsed / 1_000_000_000_000, "T");
  }
  if (absValue >= 1_000_000_000) {
    return formatScaled(parsed / 1_000_000_000, "B");
  }
  if (absValue >= 1_000_000) {
    return formatScaled(parsed / 1_000_000, "M");
  }
  if (absValue >= 1_000) {
    return formatScaled(parsed / 1_000, "k");
  }
  if (absValue > 0 && absValue < 1) {
    return parsed.toFixed(maxDecimals);
  }
  return formatNumberWithSpaces(parsed, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
};

export const formatFiatWithTwoDecimals = (value: string | number): string => {
  if (!value && value !== 0) {
    return "R 0.00";
  }

  let numericValue: number;
  if (typeof value === "string") {
    const cleaned = value
      .trim()
      .replace(/[^\d.,-]/g, "")
      .replace(/,/g, "");
    numericValue = parseFloat(cleaned);
    if (isNaN(numericValue)) {
      return "R 0.00";
    }
  } else {
    numericValue = value;
  }

  return `R ${formatWithThousandSpaces(numericValue, 2)}`;
};
